// FILE: src/theme/preferences.js
// Display preferences kept per browser (plan WP-8 task 5): colour mode is
// MUI's own `mui-mode` key; text size and reduced motion are ours. Storage
// can be missing or blocked (private windows), so every access is guarded
// and the app works with the defaults. public/index.html applies the saved
// values before the first paint.
import { useCallback, useState } from "react";

export const TEXT_SIZE_KEY = "zun.textSize";
export const REDUCE_MOTION_KEY = "zun.reduceMotion";
export const TEXT_SIZES = [
  { value: "100", label: "Standard" },
  { value: "112", label: "Large" },
  { value: "125", label: "Larger" },
  { value: "150", label: "Largest" }
];

export function readPref(key, fallback = null) {
  try {
    const v = window.localStorage.getItem(key);
    return v === null ? fallback : v;
  } catch {
    return fallback;
  }
}

export function writePref(key, value) {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    /* storage blocked: the setting lasts for this page only */
  }
}

export function applyTextSize(size) {
  const ok = TEXT_SIZES.some((s) => s.value === size);
  document.documentElement.style.fontSize = ok && size !== "100" ? `${size}%` : "";
}

export function applyReduceMotion(on) {
  if (on) document.documentElement.setAttribute("data-reduce-motion", "true");
  else document.documentElement.removeAttribute("data-reduce-motion");
}

/** Text size and reduced motion, applied immediately and remembered. */
export function useDisplayPreferences() {
  const [textSize, setTextSizeState] = useState(() => readPref(TEXT_SIZE_KEY, "100"));
  const [reduceMotion, setReduceMotionState] = useState(() => readPref(REDUCE_MOTION_KEY, "false") === "true");

  const setTextSize = useCallback((size) => {
    setTextSizeState(size);
    writePref(TEXT_SIZE_KEY, size);
    applyTextSize(size);
  }, []);

  const setReduceMotion = useCallback((on) => {
    setReduceMotionState(on);
    writePref(REDUCE_MOTION_KEY, on ? "true" : "false");
    applyReduceMotion(on);
  }, []);

  return { textSize, setTextSize, reduceMotion, setReduceMotion };
}
